import './assets/datatables.min.css';
import DataTable from 'datatables.net-dt';
import getTemplate from './bk-data-table-template';
import { displayErrorAndLog } from '../../../../modules/bk/events/bk-error';
import { t } from '../../../../modules/bk/component-property-providers/bk-translations';

class BkDataTable extends HTMLElement {
    static SelectionChangeEvent = 'BkDataTableSelectionChangeEvent';

    #dataTableInstance;
    #tableId;
    #options = {
        autoWidth: false,
        cache: false,
        language: {
            zeroRecords: t('cs.text.noResults'),
        },
        paging: false,
        searching: false,
        info: false,
    };
    #isRowSelectable;
    #isRowDisabled;

    constructor() {
        super();
    }

    connectedCallback() {
        this.#tableId = this.getAttribute('bk-id');
        if (!this.#tableId) {
            throw new Error(`${this.tagName}: missing table id`);
        }
        const ariaLabelId = this.getAttribute('bk-aria-label-id');
        const ariaLabelledBy = ariaLabelId && document.querySelector(`#${ariaLabelId}`) ? ariaLabelId : '';
        const content = getTemplate(this.#tableId, ariaLabelledBy).content.cloneNode(true);
        this.append(content);
    }

    isDataTable() {
        return !!this.#dataTableInstance;
    }

    reload(data) {
        if (!this.#dataTableInstance) {
            return;
        }
        data ? this.#dataTableInstance.clear().rows.add(data).draw() : this.#dataTableInstance.ajax?.reload();
    }

    enableRowsSelection() {
        this.#dataTableInstance.on('click', 'tbody tr', (e) => {
            if (
                this.#isRowDisabled &&
                this.#isRowDisabled instanceof Function &&
                this.#isRowDisabled(this.#dataTableInstance.row(e.currentTarget).data())
            ) {
                e.preventDefault();

                return;
            }
            if (this.#isRowSelectable && this.#isRowSelectable instanceof Function) {
                const selectedData = this.#dataTableInstance.row(e.currentTarget).data();
                if (this.#isRowSelectable(selectedData)) {
                    e.currentTarget.classList.toggle('selected');
                } else {
                    e.currentTarget.classList.remove('selected');
                }
            } else {
                e.currentTarget.classList.toggle('selected');
            }

            const data = this.#dataTableInstance.rows('.selected').data();
            const event = new Event(BkDataTable.SelectionChangeEvent);
            event.data = data;
            this.dispatchEvent(event);
        });
    }

    set isRowDisabled(checkFunction) {
        this.#isRowDisabled = checkFunction;
    }

    set isRowSelectable(checkFunction) {
        this.#isRowSelectable = checkFunction;
    }

    /**
     * initializes the DataTable - data must be an array. if ajax option is used without dataSrc pointing to an array in the response, the response has to have the format { data: []}
     * @param {Array.<{[attributes]: {[attributeName]: string}, content: string}>} columns - a table head (th) is added for each entry, with the provided content and optional attributes
     * @param {Object} options - DataTable options {@link https://datatables.net/manual/options}, some default options are set in BkDataTable.#options
     */
    initDataTable(columns, options) {
        if (this.#dataTableInstance) {
            return;
        }

        this.#setOptions(options);
        this.#addColumns(columns);
    }

    // TODO: make default initComplete option?
    dispatchInitComplete() {
        this.dispatchEvent(
            new CustomEvent('bk-table-init-complete', {
                bubbles: true,
                detail: { tableId: this.#tableId },
            }),
        );
    }

    #setOptions(options) {
        if (options) {
            this.#options = { ...this.#options, ...options };
        }
        if (this.#options.ajax && !this.#options.ajax.error) {
            this.#options.ajax.error = (jqXHR, ajaxOptions, thrownError) => {
                displayErrorAndLog(thrownError);
            };
        }
    }

    #addColumns(columns) {
        columns.forEach((col) => {
            const th = document.createElement('th');
            th.innerHTML = col.content;

            if (col.attributes && Object.keys(col.attributes).length) {
                Object.keys(col.attributes).forEach((attributeName) => {
                    th.setAttribute(attributeName, col.attributes[attributeName]);
                });
            }

            this.querySelector('tr').appendChild(th);
        });

        this.#dataTableInstance = new DataTable(`#${this.#tableId}`, this.#options).columns.adjust();
    }

    selectRows(conditionFunction) {
        this.#dataTableInstance.rows().every(function () {
            if (conditionFunction(this.data())) {
                this.node().classList.add('selected');
            }
        });
    }
}

export default BkDataTable;
